import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Field, Input, Select, Textarea } from "@/components/Ui";

const TOPICS = [
  ["account", "Account or sign-in"],
  ["rfq", "RFQ or quotation"],
  ["product", "Product data or equivalents"],
  ["subscription", "Subscription or invoice"],
  ["technical", "Technical problem with the platform"],
  ["other", "Something else"],
];

export default function GetHelp() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    company: user?.company?.name || "",
    phone: "",
    topic: "other",
    subject: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // AuthContext resolves after mount on a direct load, so backfill fields the user hasn't typed in
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
      company: f.company || user.company?.name || "",
    }));
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/support/requests", form);
      setDone(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-16" data-testid="get-help-page">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
        Support
      </p>
      <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-[#102A43] sm:text-5xl">
        Get help
      </h1>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#667788] md:text-base">
        Tell us what you need and the ADIT team will reply by email. Use this for account
        problems, technical data queries, quotations, invoices or anything that is not working
        as expected.
      </p>

      <Card className="mt-8">
        {done ? (
          <div className="py-6 text-center" data-testid="get-help-sent">
            <MailCheck className="mx-auto h-10 w-10 text-[#13795B]" />
            <p className="mt-4 font-display text-lg font-bold text-[#102A43]">
              Request {done.reference} logged
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm text-[#667788]">{done.status}</p>
            <Link to="/" className="mt-6 inline-block">
              <Btn variant="ghost" data-testid="get-help-home-btn">Back to the homepage</Btn>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Your name">
                <Input data-testid="help-name-input" required minLength={2}
                  value={form.name} onChange={set("name")} />
              </Field>
              <Field label="Email for our reply">
                <Input data-testid="help-email-input" type="email" required
                  value={form.email} onChange={set("email")} />
              </Field>
              <Field label="Company (optional)">
                <Input data-testid="help-company-input" value={form.company} onChange={set("company")} />
              </Field>
              <Field label="Phone / WhatsApp (optional)">
                <Input data-testid="help-phone-input" value={form.phone} onChange={set("phone")} />
              </Field>
              <Field label="What is this about?" className="sm:col-span-2">
                <Select data-testid="help-topic-select" options={TOPICS}
                  value={form.topic} onChange={set("topic")} />
              </Field>
              <Field label="Subject" className="sm:col-span-2">
                <Input data-testid="help-subject-input" required minLength={3}
                  placeholder="Short summary, e.g. Cannot upload RFQ attachment"
                  value={form.subject} onChange={set("subject")} />
              </Field>
              <Field label="How can we help?" className="sm:col-span-2"
                hint="Include RFQ or part numbers where relevant — it helps us answer faster.">
                <Textarea data-testid="help-message-input" rows={6} required minLength={10}
                  value={form.message} onChange={set("message")} />
              </Field>
            </div>
            {error && (
              <p data-testid="help-error"
                className="mt-4 rounded-[9px] bg-[#fdecec] px-3 py-2 text-sm text-[#B42318]">
                {error}
              </p>
            )}
            <Btn data-testid="help-submit-btn" loading={busy} className="mt-6 w-full sm:w-auto">
              <LifeBuoy className="h-4 w-4" /> Send to the ADIT team
            </Btn>
          </form>
        )}
      </Card>
    </div>
  );
}
